import { useEffect, useState } from "react";
import { ChevronDown, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Role + bio editor for one team member on /admin/team.
 * Built-in people: empty text = back to the site default (data/content.js).
 * Admin-added people (`person.custom`): the record IS the source, so the
 * alma mater caption is editable too and a role is required.
 */
export const TeamBioEditor = ({ person, memberKey, override, client, onSaved }) => {
  const custom = Boolean(person.custom);
  const base = custom ? { role: "", bio: "", school: "" } : person;
  const current = {
    role: override?.role || base.role || "",
    bio: override?.bio || base.bio || "",
    school: override?.school || base.school || "",
  };
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(current.role);
  const [bio, setBio] = useState(current.bio);
  const [school, setSchool] = useState(current.school);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRole(current.role);
    setBio(current.bio);
    setSchool(current.school);
  }, [current.role, current.bio, current.school]);

  const customText = !custom && Boolean(override?.role || override?.bio);
  const dirty = role !== current.role || bio !== current.bio || (custom && school !== current.school);
  const paragraphs = bio.split(/\n\s*\n/).filter((p) => p.trim()).length;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        role: !custom && role.trim() === (base.role || "").trim() ? null : role,
        bio: !custom && bio.trim() === (base.bio || "").trim() ? null : bio,
      };
      if (custom) body.school = school;
      const res = await client.put(`/admin/team/photos/${memberKey}`, body);
      onSaved(memberKey, res.data);
      toast.success(`${person.name}: text saved.`, { description: "Live on /team now." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the text.");
    } finally {
      setSaving(false);
    }
  };

  const resetText = async () => {
    setSaving(true);
    try {
      const res = await client.put(`/admin/team/photos/${memberKey}`, { role: null, bio: null });
      onSaved(memberKey, res.data);
      toast.success(`${person.name}: role and bio back to the site default.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset.");
    } finally {
      setSaving(false);
    }
  };

  const input = "mt-1 w-full rounded-md border border-border bg-sand px-3 py-2 text-ink outline-none focus:border-ink";

  return (
    <div className="mt-5 border-t border-border pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`team-bio-editor-toggle-${memberKey}`}
        className="flex w-full items-center justify-between text-left text-[12px] font-semibold text-ink"
      >
        <span>
          Role &amp; bio
          {!custom && <span className="ml-2 text-[11px] font-medium text-olive">{customText ? "· custom text" : "· site default"}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-olive transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`team-bio-editor-${memberKey}`}>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">Role</span>
            <input value={role} onChange={(e) => setRole(e.target.value)} maxLength={120} data-testid={`team-role-input-${memberKey}`}
              className={`${input} font-serif text-lg italic`} />
          </label>
          {custom && (
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">Alma mater <span className="normal-case tracking-normal">(polaroid caption)</span></span>
              <input value={school} onChange={(e) => setSchool(e.target.value)} maxLength={80} data-testid={`team-school-input-${memberKey}`}
                className={`${input} text-[14px]`} />
            </label>
          )}
          <label className="block">
            <span className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">
              Bio <span className="font-mono normal-case tracking-normal">{paragraphs} {paragraphs === 1 ? "paragraph" : "paragraphs"} · {bio.length}/6000</span>
            </span>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={6000} rows={8} data-testid={`team-bio-input-${memberKey}`}
              className={`${input} resize-y text-[14px] leading-relaxed`} />
            <span className="mt-1 block text-[11px] text-olive">Leave a blank line between paragraphs. The first paragraph shows on the page; the rest sit behind &ldquo;More about {person.name.split(" ")[0]}&rdquo;.</span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={save} disabled={saving || !dirty} data-testid={`team-bio-save-${memberKey}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[12px] font-semibold text-sand transition hover:bg-forest disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save text
            </button>
            {customText && (
              <button type="button" onClick={resetText} disabled={saving} data-testid={`team-bio-reset-${memberKey}`}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold text-olive transition hover:text-ink disabled:opacity-60">
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
                Reset text to site default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
